import { MouseEventHandler, ReactNode } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import styled from 'styled-components';
import Button from '../Button';
import { ButtonColor, ButtonSize } from '../Button/types';
import { Flex } from '../Flex';
import Menu from '../Menu/Menu';
import { componentToken } from '../../style/utils';

export type SplitAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: JSX.Element;
  tooltip?: ReactNode;
};

type Props = {
  children: ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement>;
  actions?: SplitAction[];
  menuTitle?: string;
  startIcon?: ReactNode;
  variant?: 'contained' | 'outlined';
  color?: ButtonColor;
  size?: ButtonSize;
  disabled?: boolean;
  className?: string;
};

const SIZE_TO_HEIGHT: Record<ButtonSize, number> = {
  extraSmall: 28,
  medium: 32,
  large: 40,
};

const Group = styled(Flex)`
  align-items: stretch;
  width: fit-content;
`;

const PrimaryButton = styled(Button)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const CaretButton = styled(Button)<{ $dimension: number }>`
  min-width: ${({ $dimension }) => $dimension}px;
  width: ${({ $dimension }) => $dimension}px;
  padding: 0;
  margin-left: -${componentToken('splitButton.caretButton.marginLeft')};
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 1px solid
    ${({ theme, variant }) =>
      variant === 'outlined'
        ? theme.tokens.components.splitButton.caretButton.outlinedBorderLeft
        : theme.tokens.components.splitButton.caretButton.containedBorderLeft};
`;

const SplitButton = ({
  children,
  onClick,
  actions,
  menuTitle,
  startIcon,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  disabled,
  className,
}: Props) => {
  if (!actions?.length) {
    return (
      <Button
        className={className}
        variant={variant}
        color={color}
        size={size}
        startIcon={startIcon}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </Button>
    );
  }

  return (
    <Group className={className}>
      <PrimaryButton
        variant={variant}
        color={color}
        size={size}
        startIcon={startIcon}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </PrimaryButton>

      <Menu
        menuButton={
          <CaretButton
            variant={variant}
            color={color}
            size={size}
            $dimension={SIZE_TO_HEIGHT[size]}
            disabled={disabled}
            aria-label="More options"
          >
            <KeyboardArrowDown
              sx={{ fontSize: componentToken('splitButton.root.fontSize') }}
            />
          </CaretButton>
        }
        options={actions.map((action, index) => ({
          value: String(index),
          label: action.label,
          disabled: action.disabled,
          icon: action.icon,
          tooltip: action.tooltip,
        }))}
        menuTitle={menuTitle}
        closeOnChange
        sx={{ mt: 1 }}
        onChange={(value) => actions[Number(value)]?.onClick()}
      />
    </Group>
  );
};

export default SplitButton;
